
import { useState, useCallback, useEffect } from "react";
import { RichTextEditor } from "../../shared/RichTextEditor";
import { formatDetailedNotes, parseJsonSafely } from '../../../lib/formatting-utils';
import { useToast } from "../../../hooks/use-toast";
import { meetingNotesService } from "../../../services/supabase/meeting-notes";
import React from "react";

interface DetailedNotesSectionProps {
  notes: string | Record<string, any> | null | undefined;
  meetingId?: string;
  userId?: string;
}

interface DetailedNote {
  topic: string;
  details: string;
}

export function DetailedNotesSection({ notes, meetingId, userId }: DetailedNotesSectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();
  const [contentReady, setContentReady] = useState(false);
  const [localContent, setLocalContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Initialize content when component mounts or notes change
  useEffect(() => {
    try {
      console.log('Initializing detailed notes');
      setError(null);
      
      // Handle undefined or null notes
      if (notes === undefined || notes === null) {
        const emptyContent = formatDetailedNotes([]);
        setLocalContent(emptyContent);
        setContentReady(true);
        return;
      }
      
      // Check if notes is already formatted HTML
      if (typeof notes === 'string' && 
          notes.trim().startsWith('<') && 
          notes.includes('</')) {
        setLocalContent(notes);
        setContentReady(true);
        return;
      }

      // Parse notes data
      let parsedNotes: any = null;
      
      if (typeof notes === 'string') {
        try {
          parsedNotes = JSON.parse(notes);
        } catch (e) {
          console.warn('Notes is not valid JSON, treating as string:', e);
          // If it's not JSON but has content, use basic formatting
          if (notes.trim()) {
            setLocalContent(`<h2>Notes</h2><p>${notes}</p>`);
            setContentReady(true);
            return;
          }
        }
      } else {
        parsedNotes = notes;
      }
      
      // Handle both array format and nested object format
      if (Array.isArray(parsedNotes)) {
        const formattedContent = formatDetailedNotes(parsedNotes);
        setLocalContent(formattedContent);
      } else if (parsedNotes?.detailed_notes) {
        const formattedContent = formatDetailedNotes(parsedNotes.detailed_notes);
        setLocalContent(formattedContent);
      } else {
        const emptyContent = formatDetailedNotes([]);
        setLocalContent(emptyContent);
      }
      
      console.log('Detailed notes content ready');
      setContentReady(true);
    } catch (error) {
      console.error('Error initializing detailed notes:', error);
      setError('Error loading notes content');
      const emptyContent = formatDetailedNotes([]);
      setLocalContent(emptyContent);
      setContentReady(true);
    }
  }, [notes]);
  
  const handleEditorChange = useCallback(async (content: string) => {
    console.log('Editor content changed');
    setLocalContent(content);
  }, []);

  const handleBlur = async () => {
    if (!meetingId || !userId) {
      console.error('Missing meetingId or userId');
      toast({
        title: "Error",
        description: "Could not save notes: missing meeting or user information",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      const formattedData = parseEditedContent(localContent);
      console.log('Formatted notes data before save:', formattedData.length);
      
      if (!formattedData || formattedData.length === 0) {
        throw new Error("No valid content to save");
      }
      
      // Serialize the data before saving
      await meetingNotesService.updateField(
        meetingId,
        userId,
        'detailed_notes',
        JSON.stringify(formattedData)
      );

      toast({
        title: "Success",
        description: "Notes saved successfully",
      });
    } catch (error) {
      console.error('Error saving notes:', error);
      toast({
        title: "Error saving changes",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setIsEditing(false);
    }
  };

  const parseEditedContent = (content: string): DetailedNote[] => {
    try {
      if (!content || content.trim() === '') {
        return [{ topic: 'Notes', details: '<p>No detailed notes available</p>' }];
      }
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');
      const notes: DetailedNote[] = [];
      
      // Process all h2 headers and their content
      const headings = doc.querySelectorAll('h2');
      
      if (headings.length === 0) {
        // No headers found, treat entire content as single note
        return [{ 
          topic: 'Notes', 
          details: content 
        }];
      }
      
      headings.forEach((heading, index) => {
        const topic = heading.textContent || 'Untitled Section';
        let details = '';
        
        // Collect all elements until the next heading
        let element = heading.nextElementSibling;
        while (element && element.tagName !== 'H2') {
          details += element.outerHTML;
          element = element.nextElementSibling;
        }
        
        if (!details) {
          details = '<p>No details available</p>';
        }
        
        notes.push({ topic, details });
      });
      
      // If no notes were parsed, create a default empty one
      if (notes.length === 0) {
        notes.push({ topic: 'Notes', details: '<p>No detailed notes available</p>' });
      }
      
      return notes;
    } catch (error) {
      console.error('Error parsing content:', error);
      return [{ topic: 'Notes', details: '<p>Error parsing notes</p>' }];
    }
  };

  // Error state
  if (error) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Detailed Notes</h3>
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg">
          <p>{error}</p>
          <button 
            onClick={() => {
              setError(null);
              setContentReady(false);
              
              // Try to reinitialize with empty content
              const emptyContent = formatDetailedNotes([]);
              setLocalContent(emptyContent);
              setContentReady(true);
            }} 
            className="text-sm underline mt-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (!contentReady) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Detailed Notes</h3>
        <div className="animate-pulse p-4 bg-muted/50 rounded-lg h-32"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Detailed Notes</h3>
      {isEditing ? (
        <div className="relative">
          <RichTextEditor
            initialContent={localContent || ""}
            onChange={handleEditorChange}
            onBlur={handleBlur}
            placeholder="Add detailed notes..."
          />
          {isLoading && (
            <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={() => setIsEditing(true)}
          className="prose prose-sm max-w-none dark:prose-invert cursor-pointer hover:bg-muted/50 rounded-lg p-4"
          dangerouslySetInnerHTML={{ 
            __html: localContent || "<p>Add detailed notes...</p>"
          }}
        />
      )}
    </div>
  );
}
